import React, { useCallback, useEffect, useRef, useState } from 'react';
import './GenDefaults.css';
import GenConfig from '../../core/GenConfig';
import GenConfPanel from '../generator/GenConfPanel';
import { clearGenDefaults, getGenDefaults, setGenDefaults } from '../../services/genDefaults';
import { loadItemCatalog, peekItemCatalog } from '../../services/itemCatalog';

const TXT_TITLE = {
    fr: 'Paramètres avancés par défaut',
    en: 'Default advanced settings',
    de: 'Standardmäßige erweiterte Einstellungen',
    es: 'Ajustes avanzados por defecto',
    pt: 'Configurações avançadas padrão',
};
const TXT_SUBTITLE = {
    fr: 'Les prochains stuffs créés sur cet appareil seront initialisés avec ces réglages par défaut. Les stuffs existants ne changent pas.',
    en: 'New builds created on this device will start from these default settings. Existing builds are unchanged.',
    de: 'Neue Builds, die auf diesem Gerät erstellt werden, starten mit diesen Standardeinstellungen. Bestehende Builds bleiben unverändert.',
    es: 'Las próximas builds creadas en este dispositivo se iniciarán con estos ajustes por defecto. Las builds existentes no cambian.',
    pt: 'As próximas builds criadas neste dispositivo serão iniciadas com estas configurações padrão. As builds existentes não mudam.',
};
const TXT_RESET = {
    fr: 'Réinitialiser',
    en: 'Reset',
    de: 'Zurücksetzen',
    es: 'Restablecer',
    pt: 'Redefinir',
};
const TXT_LOADING = { fr: 'Chargement...', en: 'Loading...', de: 'Wird geladen...', es: 'Cargando...', pt: 'Carregando...' };

/**
 * The generator's advanced settings, edited as the starting point for the next
 * build instead of for one build. Same panel, a different config to write to —
 * which is the whole reason `GenConfPanel` takes `genConfig` / `setGenConfig`
 * rather than reaching for the generator's own state.
 */
const GenDefaults = ({ lang = 'fr' }) => {
    const [genConfig, setGenConfig] = useState(() => new GenConfig(getGenDefaults()));
    // A stored blacklist is ids, and GenConfig resolves them against the catalog
    // at construction: build the config before it lands and the chips are stuck
    // nameless. Wait, then rebuild from the same object once it is in memory.
    const [catalogReady, setCatalogReady] = useState(() => !!peekItemCatalog());
    const configRef = useRef(genConfig);
    configRef.current = genConfig;

    useEffect(() => {
        if (catalogReady) return undefined;
        let cancelled = false;
        loadItemCatalog().then(() => {
            if (cancelled) return;
            // Deliberately not through `editConfig`: resolving ids into items is
            // not an edit, and storing on it would save defaults for anyone who
            // merely opened this page.
            setGenConfig(prev => new GenConfig(prev.toObject()));
            setCatalogReady(true);
        });
        return () => { cancelled = true; };
    }, [catalogReady]);

    /**
     * What the panel writes to. Persisting here rather than in an effect keeps
     * a store to real edits, and composing against a ref rather than the state
     * updater keeps the write out of a function React may call twice — while
     * still letting one handler chain several updates, which the panel does.
     */
    const editConfig = useCallback((updater) => {
        const next = typeof updater === 'function' ? updater(configRef.current) : updater;
        configRef.current = next;
        setGenConfig(next);
        setGenDefaults(next);
    }, []);

    const onReset = useCallback(() => {
        clearGenDefaults();
        const fresh = new GenConfig({});
        configRef.current = fresh;
        setGenConfig(fresh);
    }, []);

    return (
        <section className="gen-defaults column left">
            <div className="gen-defaults-heading column left">
                <h2 className="gen-defaults-title">{TXT_TITLE[lang] || TXT_TITLE.en}</h2>
                <p className="gen-defaults-subtitle">{TXT_SUBTITLE[lang] || TXT_SUBTITLE.en}</p>
            </div>
            <div className="panel gen-defaults-panel">
                {catalogReady ? (
                    <GenConfPanel
                        lang={lang}
                        genConfig={genConfig}
                        setGenConfig={editConfig}
                        inOverlay={false}
                    />
                ) : (
                    <p className="gen-defaults-subtitle">{TXT_LOADING[lang] || TXT_LOADING.en}</p>
                )}
            </div>
            <button type="button" className="gen-defaults-reset" onClick={onReset}>
                {TXT_RESET[lang] || TXT_RESET.en}
            </button>
        </section>
    );
};

export default GenDefaults;
